<template>
  <!-- Visibility Restricted to: admin, superadmin, supervisor, operator, deviceuser -->
  <div v-if="['admin', 'superadmin', 'supervisor', 'operator', 'deviceuser'].includes(userRole)">
    <!-- ── 2. SECTION: BRANCH BROADCAST & CYCLE AUDIT (SPLIT PIE CHART VIEW) ── -->
    <div class="row mb-4">
      <div class="col-12">
        <div class="card shadow-sm border-0" style="border-radius: 1rem; min-height: 520px;">
          <div class="card-header pb-0 bg-transparent border-0 d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
            <div>
              <h6 class="font-weight-bolder mb-0" style="color: #1e3a5f;">Branch Broadcast & Cycle Audit</h6>
              <p class="text-muted text-xs">Click a slice in the chart to view specific branch details and deep audit logs on the right.</p>
            </div>

            <!-- ── SMART EXPORT DROPDOWN (Manual Vue Management) ── -->
            <div class="d-flex gap-2">
              <div class="dropdown position-relative">
                <button class="btn btn-sm btn-outline-primary mb-0 d-flex align-items-center" 
                        type="button" 
                        @click.stop="showExportDropdown = !showExportDropdown"
                        style="border-radius: 8px; text-transform: none; border: 1px solid #d2d6da;">
                  <i class="ni ni-cloud-download-95 me-2"></i>
                  {{ activeBranchForAudit ? 'Export Branch Audit' : 'Export All Data' }}
                  <i class="ni ni-bold-down ms-2" style="font-size: 0.7rem;"></i>
                </button>
                
                <!-- Dropdown Menu managed by Vue State -->
                <ul class="dropdown-menu dropdown-menu-end shadow border-0" 
                    :class="{ 'show d-block': showExportDropdown }"
                    style="border-radius: 10px; min-width: 200px; top: 100%; right: 0; margin-top: 5px; z-index: 1050; padding: 10px 0;">
                  <li>
                    <h6 class="dropdown-header text-uppercase text-secondary text-xxs font-weight-bolder opacity-7">Select Format</h6>
                  </li>
                  <li>
                    <a class="dropdown-item text-xs font-weight-bold py-2 px-3" href="javascript:;" @click="handleExcelExport">
                      <i class="ni ni-collection text-success me-2"></i> Excel (.xlsx)
                    </a>
                  </li>
                  <li>
                    <a class="dropdown-item text-xs font-weight-bold py-2 px-3" href="javascript:;" @click="handlePDFExport">
                      <i class="ni ni-paper-diploma text-danger me-2"></i> PDF (.pdf)
                    </a>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div class="card-body p-4">
            <div class="row align-items-center">
              
              <!-- LEFT SIDE: PIE CHART -->
              <div class="col-lg-5 col-md-12 border-end">
                <div v-if="auditSummaryData.length > 0" class="position-relative" style="height: 400px;">
                  <canvas ref="auditPieChart"></canvas>
                </div>
                <div v-else class="text-center py-5">
                  <div class="spinner-border text-primary" role="status"></div>
                  <p class="text-muted mt-2">Loading Audit Visualizations...</p>
                </div>
                <!-- Clear Selection Logic -->
                <div v-if="activeBranchForAudit" class="text-center mt-2">
                    <button class="btn btn-link text-xs text-secondary mb-0 shadow-none border-0" @click="activeBranchForAudit = null">
                        <i class="ni ni-bold-left me-1"></i> Reset to All Branches
                    </button>
                </div>
              </div>

              <!-- RIGHT SIDE: BRANCH DETAIL PANEL -->
              <div class="col-lg-7 col-md-12 ps-lg-5 mt-4 mt-lg-0">
                <div v-if="activeBranchForAudit">
                  <!-- Detail Header -->
                  <div class="d-flex justify-content-between align-items-center mb-3 pb-3 border-bottom">
                    <div>
                      <h6 class="mb-0 font-weight-bold text-dark" style="font-size: 1.1rem;">
                        <i class="ni ni-building me-2 text-primary"></i>{{ activeBranchForAudit.branch_name }}
                      </h6>
                      <small class="text-muted">{{ activeBranchForAudit.city }}, {{ activeBranchForAudit.state }}</small>
                    </div>
                    <div class="text-end">
                        <span class="badge bg-soft-success text-success px-3 py-2 font-weight-bold" style="border-radius: 8px; font-size: 0.8rem;">
                          TOTAL: {{ activeBranchForAudit.completed_cycles_total }} LOOPS
                        </span>
                        <div v-if="activeBranchForAudit.device_count" class="text-xxs text-secondary mt-1">
                            Calculated for {{ activeBranchForAudit.device_count }} device(s)
                        </div>
                    </div>
                  </div>

                  <!-- Detail Table -->
                  <div class="table-responsive" style="max-height: 400px; overflow-y: auto;">
                    <table class="table align-items-center mb-0">
                      <thead class="sticky-top bg-white" style="z-index: 1;">
                        <tr style="border-bottom: 2px solid #f1f5f9;">
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Type</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Title</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Loop Duration</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2 text-center">Branch Plays</th>
                          <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-2">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(item, idx) in activeBranchForAudit.broadcast_cycle_audit" 
                            :key="idx" 
                            @click="openTimeDrilldown(item)" 
                            style="border-bottom: 1px solid #f8fafc; cursor: pointer;" 
                            class="audit-row-hover">
                          <td class="py-3">
                            <span class="badge text-xxs px-2 py-1" :class="getAuditTypeBadge(item.type)">{{ item.type.charAt(0).toUpperCase() }}</span>
                          </td>
                          <td class="py-3">
                            <p class="text-xs font-weight-bold mb-0 text-truncate" style="max-width: 150px;" :title="item.title">{{ item.title }}</p>
                            <small class="text-xxs text-primary font-weight-bold" @click.stop="openMediaStatsModal(item, activeBranchForAudit)">
                                {{ item.media_count || 0 }} Items <i class="ni ni-zoom-split-in"></i>
                            </small>
                          </td>
                          <td class="py-3 text-center">
                            <!-- Logic: Dynamic Duration from media display_times -->
                            <span class="text-xs font-weight-bold text-dark">{{ formatDuration(getActualLoopTime(item)) }}</span>
                          </td>
                          <td class="py-3 text-center">
                            <span class="text-xs font-weight-bold text-dark">{{ item.count }}</span>
                            <div v-if="activeBranchForAudit.device_count > 1" class="text-xxs text-muted">
                                ~{{ (item.count / activeBranchForAudit.device_count).toFixed(1) }} / screen
                            </div>
                          </td>
                          <td class="py-3">
                            <span v-if="item.status == 0" class="badge bg-soft-danger text-danger text-xxs font-weight-bold">STOPPED</span>
                            <span v-else-if="isActuallyLive(item)" class="badge bg-soft-info text-info text-xxs font-weight-bold pulse-info">LIVE</span>
                            <span v-else class="badge bg-soft-secondary text-secondary text-xxs font-weight-bold">ENDED</span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div v-else class="text-center py-5 bg-light rounded-3 d-flex flex-column align-items-center justify-content-center" style="min-height: 380px; border: 2px dashed #dee2e6;">
                  <i class="ni ni-tap-01 text-secondary opacity-3" style="font-size: 3.5rem;"></i>
                  <h6 class="text-muted mt-3">No Branch Selected</h6>
                  <p class="text-muted text-xs px-5">Please click a specific branch slice on the Pie Chart to see its playback records.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 11. ENLARGED DRILL-DOWN: XL MODAL FOR TIMESTAMPS ── -->
    <div v-if="showTimeModal" class="modal-overlay" style="z-index: 2200;">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content shadow-lg border-0" style="border-radius: 1.2rem; height: 85vh;">
          <div class="modal-header border-0 bg-white py-4 px-4 d-flex justify-content-between align-items-center">
            <div>
              <h5 class="modal-title font-weight-bolder mb-0 text-dark">
                <i class="ni ni-calendar-grid-58 me-2 text-primary"></i>Playback Audit: {{ selectedRecord?.title }}
              </h5>
              <p class="text-xs text-muted mb-0 mt-1">
                Playlist Duration: <strong>{{ formatDuration(getActualLoopTime(selectedRecord)) }}</strong> | 
                Avg. Plays per Hour/Screen: <strong>{{ (3600 / getActualLoopTime(selectedRecord)).toFixed(1) }}</strong>
              </p>
            </div>
            <!-- ADDED: Media-Wise/Date-Wise Quick Export inside Modal -->
            <div class="d-flex align-items-center gap-2 me-3">
                <button class="btn btn-sm btn-outline-success mb-0" @click="exportToExcel">
                    <i class="ni ni-collection me-1"></i> Excel
                </button>
                <button class="btn btn-sm btn-outline-danger mb-0" @click="exportToPDF">
                    <i class="ni ni-paper-diploma me-1"></i> PDF
                </button>
                <button type="button" class="btn-close text-dark border-0 bg-transparent ms-3" @click="showTimeModal = false" style="font-size: 1.8rem; cursor: pointer;">&times;</button>
            </div>
          </div>
          
          <div class="modal-body p-0" style="overflow: hidden;">
            <div class="row g-0 h-100">
              <div class="col-md-4 border-end" style="height: 100%; overflow-y: auto; background-color: #fcfdfe;">
                <div class="p-3 bg-white sticky-top border-bottom shadow-xs">
                  <small class="text-uppercase text-secondary font-weight-bolder opacity-7" style="font-size: 0.7rem; letter-spacing: 0.05em;">SELECT DATE (DAILY LOGS)</small>
                </div>
                <div v-for="day in selectedRecord?.daily_stats" :key="day.date" 
                     @click="activeDay = day"
                     :class="['p-4 cursor-pointer border-bottom d-flex justify-content-between align-items-center transition-all', activeDay?.date === day.date ? 'active-day-row' : 'hover-bg-light']">
                  <div>
                    <p class="mb-0 text-sm font-weight-bold text-dark">{{ day.date }}</p>
                    <small class="text-muted text-xs">{{ day.dayName }}</small>
                  </div>
                  <span :class="['badge rounded-pill px-3 py-2', activeDay?.date === day.date ? 'bg-white text-primary' : 'bg-primary']" style="font-size: 0.75rem;">
                    {{ day.count }} LOOPS
                  </span>
                </div>
                <div v-if="!selectedRecord?.daily_stats?.length" class="text-center py-5 text-muted text-xs">No daily records found.</div>
              </div>

              <div class="col-md-8" style="height: 100%; overflow-y: auto; background-color: #fff;">
                <div v-if="activeDay">
                  <div class="p-3 bg-light sticky-top border-bottom d-flex justify-content-between align-items-center">
                    <small class="text-uppercase text-secondary font-weight-bolder opacity-7" style="font-size: 0.7rem;">HOURLY DISTRIBUTION FOR {{ activeDay.date }}</small>
                    <span class="badge bg-soft-info text-info text-xxs font-weight-bold">{{ activeDay.dayName.toUpperCase() }}</span>
                  </div>
                  <div class="p-4">
                    <!-- CHANGED: Table replaced with Line Chart -->
                    <div style="height: 380px; position: relative;">
                      <canvas ref="hourlyLineChart"></canvas>
                    </div>
                    <div class="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                        <span class="text-sm font-weight-bolder text-dark" style="text-transform: uppercase;">Total Day Plays</span>
                        <span class="badge bg-gradient-dark text-white px-4 py-2 font-weight-bolder shadow-sm" style="font-size: 0.85rem; letter-spacing: 0.05em;">
                            {{ activeDay.count }} TOTAL
                        </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div class="modal-footer border-0 p-4 bg-white shadow-sm">
            <button class="btn btn-dark px-5 py-2 font-weight-bold" @click="showTimeModal = false" style="border-radius: 10px;">CLOSE AUDIT LOGS</button>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 12. DETAILED MEDIA MODAL ── -->
    <div v-if="showMediaStatsModal" class="modal-overlay" style="z-index: 2100;">
      <div class="modal-dialog modal-lg">
        <div class="modal-content shadow-lg border-0" style="border-radius: 1rem; border-top: 4px solid #5e72e4 !important;">
          <div class="modal-header border-0 bg-white py-3 px-4 d-flex justify-content-between align-items-center">
            <div>
              <h6 class="modal-title font-weight-bolder mb-0 text-dark">Media Audit: {{ activeMediaDetailItem?.title }}</h6>
              <p class="text-xs text-secondary mb-0">Branch: <strong>{{ activeMediaDetailBranch?.branch_name }}</strong></p>
            </div>
            <!-- ADDED: Media Export Options inside Modal -->
            <div class="d-flex align-items-center gap-2">
                <button class="btn btn-xs btn-outline-success mb-0" @click="exportToExcel">
                    Excel
                </button>
                <button class="btn btn-xs btn-outline-danger mb-0" @click="exportToPDF">
                    PDF
                </button>
                <button type="button" class="btn-close text-dark border-0 bg-transparent ms-2" @click="showMediaStatsModal = false" style="font-size: 1.2rem;">&times;</button>
            </div>
          </div>
          <div class="modal-body p-4" style="max-height: 60vh; overflow-y: auto;">
            <div class="d-flex align-items-center gap-3 bg-light p-3 rounded-3 border mb-4 shadow-sm">
                <div class="d-flex flex-column flex-grow-1">
                    <label class="text-xxs font-weight-bolder text-secondary mb-1">START DATE</label>
                    <input type="date" v-model="auditFilterStart" class="form-control form-control-sm border shadow-none" @change="fetchAuditData">
                </div>
                <div class="d-flex flex-column flex-grow-1">
                    <label class="text-xxs font-weight-bolder text-secondary mb-1">END DATE</label>
                    <input type="date" v-model="auditFilterEnd" class="form-control form-control-sm border shadow-none" @change="fetchAuditData">
                </div>
                <div class="align-self-end">
                    <button class="btn btn-sm btn-primary mb-0 px-4" @click="fetchAuditData">FILTER</button>
                </div>
            </div>

            <div class="row mb-4">
              <div v-for="(media, mIdx) in activeMediaDetailItem?.media_details" :key="mIdx" class="col-md-6 mb-3">
                <div class="card bg-light border-0 p-3 audit-row-hover cursor-pointer" style="border-radius: 0.8rem;" @click="openMediaDrilldown(media)">
                  <div class="d-flex align-items-center mb-2">
                    <span class="icon-shape icon-sm bg-white shadow-sm text-center border-radius-md d-flex align-items-center justify-content-center me-2">
                      <i :class="media.type === 'video' || media.type == 0 ? 'ni ni-button-play text-primary' : 'ni ni-image text-info'"></i>
                    </span>
                    <p class="text-xs font-weight-bold text-dark mb-0 text-truncate" :title="media.title">{{ media.title }}</p>
                  </div>
                  <div class="d-flex justify-content-between align-items-center">
                    <small class="text-secondary text-xxs uppercase">Duration</small>
                    <small class="text-dark font-weight-bold text-xs">{{ formatDuration(media.display_time || 10) }}</small>
                  </div>
                  <div class="mt-2 pt-2 border-top">
                     <small class="text-secondary text-xxs uppercase d-block">Max Plays / Device / Hr</small>
                     <small class="text-primary font-weight-bold text-xs">{{ (3600 / (media.display_time || 10)).toFixed(0) }} Plays</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer border-0 p-3 bg-light">
            <button class="btn btn-dark btn-sm mb-0 font-weight-bold px-4" @click="showMediaStatsModal = false">CLOSE</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { Chart, registerables } from "chart.js";

const XLSX = require("xlsx");
const { jsPDF } = require("jspdf");
const autoTable = require("jspdf-autotable").default || require("jspdf-autotable");

Chart.register(...registerables);

export default {
  name: "BranchAuditSection",
  props: {
    apiUrl: String,
    userRole: String
  },
  data() {
    return {
      auditSummaryData: [],
      activeBranchForAudit: null,
      showTimeModal: false,
      selectedRecord: null,
      activeDay: null,
      showMediaStatsModal: false,
      activeMediaDetailItem: null,
      activeMediaDetailBranch: null,
      auditFilterStart: "",
      auditFilterEnd: "",
      auditChart: null,
      hourlyChart: null, // Track the line chart instance
      showExportDropdown: false
    };
  },
  watch: {
    // Watch for day selection changes to update the chart
    activeDay: {
      handler(newVal) {
        if (newVal && this.showTimeModal) {
          this.renderHourlyChart();
        }
      },
      deep: true
    }
  },
  methods: {
    // ── NEW LOGIC: Calculate Loop Time based on actual Video/Image Duration ──
    getActualLoopTime(item) {
        if (!item || !item.media_details || item.media_details.length === 0) return 10;
        // Summing the actual display_time (which for videos is duration in seconds)
        return item.media_details.reduce((acc, m) => acc + parseInt(m.display_time || 10), 0);
    },

    formatDuration(seconds) {
        if (seconds < 60) return `${seconds}s`;
        const m = Math.floor(seconds / 60);
        const s = seconds % 60;
        return s === 0 ? `${m}m` : `${m}m ${s}s`;
    },

    handleExcelExport() {
      this.showExportDropdown = false;
      this.exportToExcel();
    },
    handlePDFExport() {
      this.showExportDropdown = false;
      this.exportToPDF();
    },

    renderAuditPieChart() {
      if (this.auditChart) this.auditChart.destroy();
      const ctx = this.$refs.auditPieChart;
      if (!ctx) return;
      const labels = this.auditSummaryData.map(b => b.branch_name);
      const data = this.auditSummaryData.map(b => b.completed_cycles_total);
      const colors = ["#5e72e4", "#2dce89", "#11cdef", "#fb6340", "#f5365c", "#8965e0", "#ffd600", "#adb5bd"];
      this.auditChart = new Chart(ctx, {
        type: 'pie',
        data: {
          labels: labels,
          datasets: [{
            data: data,
            backgroundColor: colors.slice(0, labels.length),
            hoverOffset: 25,
            borderWidth: 3,
            borderColor: '#ffffff'
          }]
        },
        options: {
          maintainAspectRatio: false,
          plugins: { legend: { position: 'bottom', labels: { padding: 15, font: { size: 10, weight: 'bold' } } } },
          onClick: (evt, elements) => {
            if (elements.length > 0) {
              const index = elements[0].index;
              this.activeBranchForAudit = this.auditSummaryData[index];
            }
          }
        }
      });
    },

    // ── RENDER HOURLY LINE CHART ──
    renderHourlyChart() {
      this.$nextTick(() => {
        if (this.hourlyChart) this.hourlyChart.destroy();
        const ctx = this.$refs.hourlyLineChart;
        if (!ctx || !this.activeDay) return;

        const labels = this.activeDay.hourly.map(h => h.time);
        const data = this.activeDay.hourly.map(h => h.count);

        this.hourlyChart = new Chart(ctx, {
          type: 'line',
          data: {
            labels: labels,
            datasets: [{
              label: 'Hourly Loops',
              data: data,
              borderColor: '#5e72e4',
              backgroundColor: 'rgba(94, 114, 228, 0.1)',
              fill: true,
              tension: 0.4,
              pointRadius: 4,
              pointBackgroundColor: '#5e72e4',
              borderWidth: 3
            }]
          },
          options: {
            maintainAspectRatio: false,
            responsive: true,
            plugins: {
              legend: { display: false },
              tooltip: {
                mode: 'index',
                intersect: false,
                padding: 10,
                backgroundColor: '#1e3a5f'
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                grid: { drawBorder: false, color: '#f1f5f9' },
                ticks: { color: '#adb5bd', font: { size: 11 } }
              },
              x: {
                grid: { display: false },
                ticks: { color: '#adb5bd', font: { size: 11 } }
              }
            }
          }
        });
      });
    },

    openTimeDrilldown(item) {
      this.selectedRecord = item;
      this.activeDay = (item.daily_stats && item.daily_stats.length > 0) ? item.daily_stats[0] : null;
      this.showTimeModal = true;
    },

    openMediaDrilldown(media) {
      this.showMediaStatsModal = false;
      // When drilling into specific media, we treat it as a single-item playlist for duration context
      this.selectedRecord = {
        title: media.title,
        media_details: [media], 
        daily_stats: this.activeMediaDetailItem.daily_stats
      };
      this.activeDay = (this.selectedRecord.daily_stats && this.selectedRecord.daily_stats.length > 0) ? this.selectedRecord.daily_stats[0] : null;
      this.showTimeModal = true;
    },

    openMediaStatsModal(item, branch) {
      this.activeMediaDetailItem = item;
      this.activeMediaDetailBranch = branch;
      this.showMediaStatsModal = true;
    },

    async fetchAuditData() {
      try {
        const headers = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
        let queryParams = "";
        if (this.auditFilterStart) queryParams += `&startDate=${this.auditFilterStart}`;
        if (this.auditFilterEnd) queryParams += `&endDate=${this.auditFilterEnd}`;
        const res = await axios.get(`${this.apiUrl}/dashboard/branch-audit?${queryParams}`, headers);
        if (res.data.success) {
          this.auditSummaryData = res.data.data;
          this.$nextTick(() => { this.renderAuditPieChart(); });
          if (this.activeBranchForAudit) {
              const updated = this.auditSummaryData.find(b => b.branch_id === this.activeBranchForAudit.branch_id);
              if (updated) this.activeBranchForAudit = updated;
          }
        }
      } catch (e) { console.error("Audit API Error:", e); }
    },

    isActuallyLive(item) {
      const now = new Date();
      const startDate = item.from ? new Date(item.from) : null;
      const endDate = item.to ? new Date(item.to) : null;
      if (endDate && now > endDate) return false;
      if (startDate && now < startDate) return false;
      return true;
    },

    getAuditTypeBadge(type) {
      if (type === 'playlist') return 'bg-soft-primary text-primary';
      if (type === 'emergency') return 'bg-soft-danger text-danger';
      return 'bg-soft-info text-info';
    },

    // ── DYNAMIC EXPORT LOGIC (EXCEL) ──
    exportToExcel() {
      let exportData = [];
      let filename = "Audit_Report";

      // Context 1: Detailed Playback Modal (Date-wise)
      if (this.showTimeModal && this.selectedRecord) {
          filename = `DateWise_Audit_${this.selectedRecord.title}`;
          this.selectedRecord.daily_stats.forEach(day => {
              exportData.push({
                  "Date": day.date,
                  "Day": day.dayName,
                  "Content Title": this.selectedRecord.title,
                  "Loops Played": day.count
              });
          });
      } 
      // Context 2: Media Detailed List Modal
      else if (this.showMediaStatsModal && this.activeMediaDetailItem) {
          filename = `Media_Audit_${this.activeMediaDetailItem.title}`;
          this.activeMediaDetailItem.media_details.forEach(media => {
              exportData.push({
                  "Branch": this.activeMediaDetailBranch.branch_name,
                  "Playlist": this.activeMediaDetailItem.title,
                  "Media File": media.title,
                  "Type": media.type,
                  "Total Loops": this.activeMediaDetailItem.count
              });
          });
      }
      // Context 3: Branch Selected
      else if (this.activeBranchForAudit) {
          filename = `Branch_Audit_${this.activeBranchForAudit.branch_name}`;
          this.activeBranchForAudit.broadcast_cycle_audit.forEach(item => {
              exportData.push({
                  "Branch": this.activeBranchForAudit.branch_name,
                  "Type": item.type.toUpperCase(),
                  "Title": item.title,
                  "Loops": item.count,
                  "Status": item.status == 0 ? "STOPPED" : "ACTIVE"
              });
          });
      }
      // Context 4: Full Summary
      else {
          this.auditSummaryData.forEach(branch => {
              branch.broadcast_cycle_audit.forEach(item => {
                  exportData.push({
                      "Branch": branch.branch_name,
                      "Type": item.type,
                      "Title": item.title,
                      "Total Loops": item.count
                  });
              });
          });
      }

      if (!exportData.length) return alert("No data to export");
      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Audit");
      XLSX.writeFile(workbook, `${filename}.xlsx`);
    },

    // ── DYNAMIC EXPORT LOGIC (PDF) ──
    exportToPDF() {
      const doc = new jsPDF('l', 'mm', 'a4');
      let title = "Audit Report";
      let rows = [];
      let heads = [['Field 1', 'Field 2', 'Field 3', 'Field 4']];

      if (this.showTimeModal && this.selectedRecord) {
          title = `Date-Wise Audit: ${this.selectedRecord.title}`;
          heads = [['Date', 'Day', 'Content Title', 'Loops']];
          this.selectedRecord.daily_stats.forEach(day => {
              rows.push([day.date, day.dayName, this.selectedRecord.title, day.count]);
          });
      } else if (this.showMediaStatsModal && this.activeMediaDetailItem) {
          title = `Media Audit: ${this.activeMediaDetailItem.title}`;
          heads = [['Branch', 'Playlist', 'Media Name', 'Type', 'Loops']];
          this.activeMediaDetailItem.media_details.forEach(m => {
              rows.push([this.activeMediaDetailBranch.branch_name, this.activeMediaDetailItem.title, m.title, m.type, this.activeMediaDetailItem.count]);
          });
      } else if (this.activeBranchForAudit) {
          title = `Branch Audit: ${this.activeBranchForAudit.branch_name}`;
          heads = [['Branch', 'Type', 'Title', 'Medias', 'Loops']];
          this.activeBranchForAudit.broadcast_cycle_audit.forEach(item => {
              rows.push([this.activeBranchForAudit.branch_name, item.type.toUpperCase(), item.title, item.media_count, item.count]);
          });
      } else {
          title = "Full Audit Summary";
          heads = [['Branch', 'Type', 'Title', 'Loops']];
          this.auditSummaryData.forEach(b => {
              b.broadcast_cycle_audit.forEach(i => rows.push([b.branch_name, i.type, i.title, i.count]));
          });
      }

      doc.setFontSize(14);
      doc.text(title, 14, 15);
      autoTable(doc, { startY: 20, head: heads, body: rows, theme: 'grid', headStyles: { fillColor: [30, 58, 95] } });
      doc.save(`${title.replace(/ /g, "_")}.pdf`);
    }
  },
  mounted() { 
    this.fetchAuditData(); 
    window.addEventListener('click', () => { this.showExportDropdown = false; });
  },
  beforeUnmount() { 
    if (this.auditChart) this.auditChart.destroy(); 
    if (this.hourlyChart) this.hourlyChart.destroy();
    window.removeEventListener('click', () => { this.showExportDropdown = false; });
  }
};
</script>

<style scoped>
.modal-overlay { position: fixed; top: 0; left: 0; width: 100%; height: 100%; background: rgba(0, 0, 0, 0.6); display: flex; justify-content: center; align-items: center; z-index: 2000; }
.bg-soft-info { background-color: #e7f5ff; }
.bg-soft-success { background-color: #e6fffa; }
.bg-soft-danger { background-color: #fff5f5; }
.bg-soft-primary { background-color: #e0e7ff; }
.bg-soft-secondary { background-color: #f8f9fa; }
.cursor-pointer { cursor: pointer; }
.transition-all { transition: all 0.25s ease; }
.hover-bg-light:hover { background-color: #f8fafc; transform: translateX(5px); }
.active-day-row { background-color: #5e72e4 !important; color: #fff !important; }
.active-day-row p, .active-day-row small { color: #fff !important; }
.audit-row-hover:hover { background-color: #f1f5f9 !important; }
.pulse-info { animation: pulse-blue 2s infinite; }
@keyframes pulse-blue { 0% { opacity: 1; } 50% { opacity: 0.5; } 100% { opacity: 1; } }
canvas { cursor: pointer; }
.sticky-top { top: -1px; }
.progress-bar.bg-gradient-info { background: linear-gradient(87deg, #11cdef 0, #1171ef 100%) !important; }
.bg-gradient-dark { background: linear-gradient(87deg, #212529 0, #212229 100%) !important; }
.dropdown-menu.show { display: block !important; opacity: 1; visibility: visible; transform: translateY(0); transition: opacity 0.2s; }
.dropdown-item { transition: all 0.2s; }
.dropdown-item:hover { background-color: #f8fafc; color: #5e72e4 !important; }
</style>